import type { Step } from '../types';
import { makeStep } from './defaults';
import {
  alternate,
  doubleUp,
  everyOther,
  lockTo,
  mirrorBlocks,
  outsideIn,
  reverseBlocks,
  rotateBlocks,
  shuffleBlocks,
  swapBlocks,
  swapPairs,
  type OrderFn,
} from './presetOrders';

/**
 * Pattern presets.
 *
 * Every preset is a *generator*, not stored data: it builds a fresh step list
 * from the track's current slice count. That's what makes `straight` a
 * dependable return point — there is no stored copy to corrupt, rename or
 * delete, and it always matches whatever loop the track is holding, whether
 * that's a 16-slice one-bar break or a 128-slice eight-bar chop.
 *
 * Every preset starts from `base(ctx)`, which is the whole mix-and-match
 * mechanism: with `layer` off it returns a straight chop, so a preset always
 * produces the same predictable result; with `layer` on it returns the pattern
 * you already have, so the preset stacks on top. Presets need no knowledge of
 * which mode they're in.
 */

export interface PresetContext {
  /** Steps in the pattern being replaced. */
  length: number;
  /** Slices available on the track's source. */
  slices: number;
  /** Steps per beat. */
  division: number;
  beatsPerBar: number;
  /** The pattern as it stands, used as the base when layering. */
  steps: Step[];
  /** Build on the existing pattern instead of a straight chop. */
  layer: boolean;
}

export interface PatternPreset {
  id: string;
  name: string;
  group: string;
  description: string;
  build(ctx: PresetContext): Step[];
}

/** Straight chop, or the current pattern when mixing. */
function base(ctx: PresetContext): Step[] {
  if (ctx.layer && ctx.steps.length) return ctx.steps.map((s) => ({ ...s }));
  const slices = Math.max(1, ctx.slices);
  return Array.from({ length: Math.max(1, ctx.length) }, (_, i) => makeStep(i % slices));
}

/**
 * Rearranges whole steps, not just slice numbers.
 *
 * Moving the step wholesale is what makes reorders compose: layer "swap bars
 * 3 & 4" over a pattern with rolls in bar 4 and the rolls travel with it.
 * `indexOf(i, len)` returns which step should be heard at position `i`.
 */
function permute(ctx: PresetContext, indexOf: (i: number, len: number) => number): Step[] {
  const s = base(ctx);
  const source = s.map((st) => ({ ...st }));
  s.forEach((st, i) => {
    const donor = source[indexOf(i, s.length)];
    if (donor) Object.assign(st, donor, { id: st.id });
  });
  return s;
}

/**
 * Runs several presets back to back, each one layering onto the last.
 *
 * This is the Mix toggle used from the inside: a recipe is literally "these
 * presets, mixed". It also means a recipe inherits any fix to the presets it's
 * built from, and honours the user's own Mix setting — `base(ctx)` decides
 * whether the chain starts from a straight chop or from what you already have.
 */
function chain(ctx: PresetContext, ids: string[]): Step[] {
  let steps = base(ctx);
  for (const id of ids) {
    const preset = PATTERN_PRESETS.find((p) => p.id === id);
    if (preset) steps = preset.build({ ...ctx, steps, layer: true });
  }
  return steps;
}

/** Position through the pattern, 0..1. */
const progress = (i: number, len: number) => (len <= 1 ? 0 : i / (len - 1));

const stepsPerBar = (ctx: PresetContext) => Math.max(1, ctx.division * ctx.beatsPerBar);
const halfBar = (ctx: PresetContext) => Math.max(1, Math.floor(stepsPerBar(ctx) / 2));
const perBeat = (ctx: PresetContext) => Math.max(1, ctx.division);

/**
 * Reorders equal-sized blocks of steps. `size` picks the scale — a bar, half a
 * bar, a beat, a single step — and `order` decides what plays where.
 */
function reorderBlocks(ctx: PresetContext, size: number, order: OrderFn): Step[] {
  const n = Math.max(1, Math.floor(Math.max(1, ctx.length) / size));
  const map = order(n);
  return permute(ctx, (i) => {
    const block = Math.floor(i / size);
    return (map[block] ?? block) * size + (i % size);
  });
}

/** Shorthand for the three scales that presets actually use. */
const bars = (ctx: PresetContext, order: OrderFn) => reorderBlocks(ctx, stepsPerBar(ctx), order);
const halves = (ctx: PresetContext, order: OrderFn) => reorderBlocks(ctx, halfBar(ctx), order);
const beats = (ctx: PresetContext, order: OrderFn) => reorderBlocks(ctx, perBeat(ctx), order);


export const PATTERN_PRESETS: PatternPreset[] = [
  // ── Basics ───────────────────────────────────────────────────────────────
  {
    id: 'straight',
    name: 'Straight',
    group: 'Steps',
    description: 'The untouched 1:1 chop, at the loop’s full slice count',
    build: (ctx) => {
      // Always a rebuild, never a layer — this is the reset.
      const n = Math.max(1, Math.min(128, ctx.slices || ctx.length));
      return Array.from({ length: n }, (_, i) => makeStep(i));
    },
  },
  {
    id: 'half-time',
    name: 'Half time',
    group: 'Steps',
    description: 'Each slice given two steps to breathe',
    build: (ctx) => {
      const s = permute(ctx, (i) => Math.floor(i / 2));
      s.forEach((st, i) => {
        if (i % 2 === 1) st.slice = -1;
      });
      return s;
    },
  },
  {
    id: 'double-time',
    name: 'Double time',
    group: 'Steps',
    description: 'Runs the chop twice as fast, so the loop covers two passes',
    build: (ctx) => permute(ctx, (i, len) => (i * 2) % len),
  },

  // ── Mutes & gaps ─────────────────────────────────────────────────────────
  {
    id: 'mute-4th',
    name: 'Every 4th muted',
    group: 'Mutes',
    description: 'Drops the last sixteenth of every beat',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if ((i + 1) % 4 === 0) st.mute = true;
      });
      return s;
    },
  },
  {
    id: 'mute-3rd',
    name: 'Every 3rd muted',
    group: 'Mutes',
    description: 'Three-against-four gaps that drift against the beat',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if ((i + 1) % 3 === 0) st.mute = true;
      });
      return s;
    },
  },
  {
    id: 'mute-offbeats',
    name: 'Offbeats muted',
    group: 'Mutes',
    description: 'Only the on-beats sound — instant half-speed feel',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 2 === 1) st.mute = true;
      });
      return s;
    },
  },
  {
    id: 'downbeats-only',
    name: 'Downbeats only',
    group: 'Mutes',
    description: 'One hit per beat, everything between it a rest',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % ctx.division !== 0) st.slice = -1;
      });
      return s;
    },
  },
  {
    id: 'last-beat-drop',
    name: 'Last beat dropped',
    group: 'Mutes',
    description: 'Silences beat four of every bar — leaves a hole to fall into',
    build: (ctx) => {
      const s = base(ctx);
      const per = stepsPerBar(ctx);
      s.forEach((st, i) => {
        if (i % per >= per - ctx.division) st.mute = true;
      });
      return s;
    },
  },
  {
    id: 'random-drops',
    name: 'Random drops',
    group: 'Mutes',
    description: 'Roughly a third of the steps muted at random',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st) => {
        if (Math.random() < 0.3) st.mute = true;
      });
      return s;
    },
  },
  {
    id: 'breakdown',
    name: 'Breakdown',
    group: 'Mutes',
    description: 'Plays in full, then strips to downbeats for the second half',
    build: (ctx) => {
      const s = base(ctx);
      const half = s.length / 2;
      s.forEach((st, i) => {
        if (i >= half && i % ctx.division !== 0) st.slice = -1;
      });
      return s;
    },
  },
  {
    id: 'gated',
    name: 'Gated stab',
    group: 'Mutes',
    description: 'Every slice cut to a third of its step — tight and staccato',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st) => (st.gate = 0.3));
      return s;
    },
  },
  {
    id: 'ghost',
    name: 'Ghost notes',
    group: 'Mutes',
    description: 'Offbeats dropped in level and shortened, on-beats left alone',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 2 === 1) {
          st.gain = 0.35;
          st.gate = 0.5;
        }
      });
      return s;
    },
  },

  // ── Reorder ──────────────────────────────────────────────────────────────
  {
    id: 'two-step',
    name: 'Two-step',
    group: 'Steps',
    description: 'Every slice played twice in a row',
    build: (ctx) => permute(ctx, (i) => Math.floor(i / 2)),
  },
  {
    id: 'backwards',
    name: 'Backwards',
    group: 'Steps',
    description: 'Order flipped end to end, each slice still playing forwards',
    build: (ctx) => permute(ctx, (i, len) => len - 1 - i),
  },
  {
    id: 'swap-pairs',
    name: 'Pairs swapped',
    group: 'Steps',
    description: 'Plays 2 1 4 3 6 5 — a constant tripping feel',
    build: (ctx) => permute(ctx, (i) => (i % 2 === 0 ? i + 1 : i - 1)),
  },
  {
    id: 'odd-even',
    name: 'Odds then evens',
    group: 'Steps',
    description: 'All the on-beats first, then everything that fell between',
    build: (ctx) =>
      permute(ctx, (i, len) => {
        const half = Math.ceil(len / 2);
        return i < half ? i * 2 : (i - half) * 2 + 1;
      }),
  },
  {
    id: 'outside-in',
    name: 'Outside in',
    group: 'Steps',
    description: 'First, last, second, second-last — folds the loop inwards',
    build: (ctx) =>
      permute(ctx, (i, len) => (i % 2 === 0 ? i / 2 : len - 1 - (i - 1) / 2)),
  },
  {
    id: 'skip-three',
    name: 'Every third slice',
    group: 'Steps',
    description: 'Steps through the loop in threes and wraps around',
    build: (ctx) => permute(ctx, (i, len) => (i * 3) % len),
  },
  {
    id: 'scatter',
    name: 'Scatter',
    group: 'Steps',
    description: 'On-beats held, offbeats pulled from anywhere in the loop',
    build: (ctx) => {
      const s = base(ctx);
      const slices = Math.max(1, ctx.slices);
      s.forEach((st, i) => {
        if (i % 2 === 1) st.slice = Math.floor(Math.random() * slices);
      });
      return s;
    },
  },

  // ── Bars ─────────────────────────────────────────────────────────────────
  { id: 'swap-bars-1-2', name: 'Swap bars 1 & 2', group: 'Bars',
    description: 'Plays 2 1 3 4 — starts you on the answer phrase',
    build: (ctx) => bars(ctx, swapBlocks(0, 1)) },
  { id: 'swap-bars-2-3', name: 'Swap bars 2 & 3', group: 'Bars',
    description: 'Plays 1 3 2 4 — pulls the turnaround forward',
    build: (ctx) => bars(ctx, swapBlocks(1, 2)) },
  { id: 'swap-bars-3-4', name: 'Swap bars 3 & 4', group: 'Bars',
    description: 'Plays 1 2 4 3 — the classic turnaround flip',
    build: (ctx) => bars(ctx, swapBlocks(2, 3)) },
  { id: 'swap-bars-1-3', name: 'Swap bars 1 & 3', group: 'Bars',
    description: 'Plays 3 2 1 4 — new opening, same ending',
    build: (ctx) => bars(ctx, swapBlocks(0, 2)) },
  { id: 'swap-bars-2-4', name: 'Swap bars 2 & 4', group: 'Bars',
    description: 'Plays 1 4 3 2 — answers itself early',
    build: (ctx) => bars(ctx, swapBlocks(1, 3)) },
  { id: 'bar-rotate', name: 'Rotate bars →', group: 'Bars',
    description: 'Plays 2 3 4 1 — same bars, new downbeat',
    build: (ctx) => bars(ctx, rotateBlocks(1)) },
  { id: 'bar-rotate-back', name: 'Rotate bars ←', group: 'Bars',
    description: 'Plays 4 1 2 3 — the turnaround becomes the intro',
    build: (ctx) => bars(ctx, rotateBlocks(-1)) },
  { id: 'bars-backwards', name: 'Bars backwards', group: 'Bars',
    description: 'Plays 4 3 2 1, each bar still running forwards',
    build: (ctx) => bars(ctx, reverseBlocks()) },
  { id: 'bar-shuffle', name: 'Shuffle bars', group: 'Bars',
    description: 'Whole bars dealt into a random order',
    build: (ctx) => bars(ctx, shuffleBlocks()) },
  { id: 'bars-mirror', name: 'Mirror bars', group: 'Bars',
    description: 'Plays 1 2 2 1 — out and back again',
    build: (ctx) => bars(ctx, mirrorBlocks()) },
  { id: 'bars-outside-in', name: 'Bars outside in', group: 'Bars',
    description: 'Plays 1 4 2 3 — ends first, folding inwards',
    build: (ctx) => bars(ctx, outsideIn()) },
  { id: 'bars-doubled', name: 'Each bar twice', group: 'Bars',
    description: 'Plays 1 1 2 2 — half the material, twice the weight',
    build: (ctx) => bars(ctx, doubleUp()) },
  { id: 'bars-swap-pairs', name: 'Bar pairs swapped', group: 'Bars',
    description: 'Plays 2 1 4 3 — every pair flipped',
    build: (ctx) => bars(ctx, swapPairs()) },
  { id: 'last-bar-twice', name: 'Last bar twice', group: 'Bars',
    description: 'Plays 1 2 4 4 — sits on the turnaround',
    build: (ctx) => bars(ctx, (n) => Array.from({ length: n }, (_, i) => (i === n - 2 ? n - 1 : i))) },
  { id: 'first-bar-anchor', name: 'Bar 1 every other', group: 'Bars',
    description: 'Plays 1 2 1 4 — keeps dragging you back to the top',
    build: (ctx) => bars(ctx, (n) => Array.from({ length: n }, (_, i) => (i % 2 === 0 && i > 0 ? 0 : i))) },
  { id: 'bars-alternate-1-3', name: 'Alternate bars 1 & 3', group: 'Bars',
    description: 'Plays 1 3 1 3 — two bars pretending to be four',
    build: (ctx) => bars(ctx, alternate(0, 2)) },
  { id: 'one-bar-loop', name: 'Lock to bar 1', group: 'Bars',
    description: 'Every bar replaced by the first — a one-bar loop out of a longer one',
    build: (ctx) => bars(ctx, lockTo(0)) },
  { id: 'lock-bar-2', name: 'Lock to bar 2', group: 'Bars',
    description: 'The second bar, over and over',
    build: (ctx) => bars(ctx, lockTo(1)) },
  { id: 'lock-last-bar', name: 'Lock to last bar', group: 'Bars',
    description: 'The turnaround, over and over',
    build: (ctx) => bars(ctx, lockTo(-1)) },
  {
    id: 'every-other-bar-reverse', name: 'Every other bar reversed', group: 'Bars',
    description: 'Bars 2 and 4 play their slices backwards',
    build: (ctx) => {
      const s = base(ctx);
      const per = stepsPerBar(ctx);
      s.forEach((st, i) => {
        if (Math.floor(i / per) % 2 === 1) st.reverse = true;
      });
      return s;
    },
  },
  {
    id: 'junglify', name: 'Junglify', group: 'Bars',
    description: 'Amen-style: quarters resequenced, offbeats flipped, roll to finish',
    build: (ctx) => {
      const per = stepsPerBar(ctx);
      const q = Math.max(1, Math.floor(per / 4));
      // Swap the middle two quarters of every bar — the move that turns a
      // straight break into a jungle chop.
      const s = reorderBlocks(ctx, q, (n) =>
        Array.from({ length: n }, (_, i) => {
          const within = i % 4;
          const mapped = within === 1 ? 2 : within === 2 ? 1 : within;
          return Math.floor(i / 4) * 4 + mapped;
        }),
      );
      s.forEach((st, i) => {
        if (i % (q * 2) === q * 2 - 1) st.reverse = true;
        if (i % 2 === 1) {
          st.gain = 0.7;
          st.gate = 0.75;
        }
      });
      const tail = [3, 4];
      tail.forEach((count, k) => {
        const st = s[s.length - tail.length + k];
        if (!st) return;
        st.repeat = count;
        st.repeatRamp = -0.5;
        st.gain = 1;
        st.gate = 1;
      });
      return s;
    },
  },

  // ── Half bars ────────────────────────────────────────────────────────────
  { id: 'half-bar-swap', name: 'Swap halves in each bar', group: 'Half bars',
    description: 'Second half of every bar played first',
    build: (ctx) => halves(ctx, swapPairs()) },
  { id: 'halves-backwards', name: 'Half bars backwards', group: 'Half bars',
    description: 'Every half-bar in reverse order, each still forwards',
    build: (ctx) => halves(ctx, reverseBlocks()) },
  { id: 'halves-shuffle', name: 'Shuffle half bars', group: 'Half bars',
    description: 'Half-bars dealt at random — busier than shuffling whole bars',
    build: (ctx) => halves(ctx, shuffleBlocks()) },
  { id: 'halves-rotate', name: 'Rotate halves →', group: 'Half bars',
    description: 'Everything shifted along by half a bar',
    build: (ctx) => halves(ctx, rotateBlocks(1)) },
  { id: 'halves-rotate-back', name: 'Rotate halves ←', group: 'Half bars',
    description: 'Half a bar earlier — lands the phrase off the downbeat',
    build: (ctx) => halves(ctx, rotateBlocks(-1)) },
  { id: 'halves-doubled', name: 'Each half twice', group: 'Half bars',
    description: 'A A B B — call and response at half-bar scale',
    build: (ctx) => halves(ctx, doubleUp()) },
  { id: 'halves-mirror', name: 'Mirror halves', group: 'Half bars',
    description: 'A B B A across the pattern',
    build: (ctx) => halves(ctx, mirrorBlocks()) },
  { id: 'halves-outside-in', name: 'Halves outside in', group: 'Half bars',
    description: 'First, last, second, second-last',
    build: (ctx) => halves(ctx, outsideIn()) },
  { id: 'halves-every-other', name: 'Every other half', group: 'Half bars',
    description: 'Drops the second half of each bar and repeats the first',
    build: (ctx) => halves(ctx, everyOther()) },
  { id: 'lock-first-half', name: 'Lock to first half', group: 'Half bars',
    description: 'The opening half-bar, looped',
    build: (ctx) => halves(ctx, lockTo(0)) },
  { id: 'lock-last-half', name: 'Lock to last half', group: 'Half bars',
    description: 'The closing half-bar, looped',
    build: (ctx) => halves(ctx, lockTo(-1)) },
  { id: 'halves-alternate', name: 'Alternate halves 1 & 3', group: 'Half bars',
    description: 'Bounces between two half-bars a bar apart',
    build: (ctx) => halves(ctx, alternate(0, 2)) },

  // ── Beats ────────────────────────────────────────────────────────────────
  { id: 'beats-backwards', name: 'Beats backwards', group: 'Beats',
    description: 'Beat order reversed, each beat still forwards',
    build: (ctx) => beats(ctx, reverseBlocks()) },
  { id: 'beats-swap-pairs', name: 'Beat pairs swapped', group: 'Beats',
    description: 'Plays 2 1 4 3 at beat scale — a constant trip',
    build: (ctx) => beats(ctx, swapPairs()) },
  { id: 'beats-rotate', name: 'Push a beat →', group: 'Beats',
    description: 'Everything a beat later, wrapping round',
    build: (ctx) => beats(ctx, rotateBlocks(1)) },
  { id: 'beats-rotate-back', name: 'Pull a beat ←', group: 'Beats',
    description: 'Everything a beat earlier',
    build: (ctx) => beats(ctx, rotateBlocks(-1)) },
  { id: 'beats-shuffle', name: 'Shuffle beats', group: 'Beats',
    description: 'Beats dealt at random across the pattern',
    build: (ctx) => beats(ctx, shuffleBlocks()) },
  { id: 'beats-doubled', name: 'Each beat twice', group: 'Beats',
    description: 'Every beat stated twice before moving on',
    build: (ctx) => beats(ctx, doubleUp()) },
  { id: 'beats-mirror', name: 'Mirror beats', group: 'Beats',
    description: 'Out to the middle and back',
    build: (ctx) => beats(ctx, mirrorBlocks()) },
  { id: 'beats-outside-in', name: 'Beats outside in', group: 'Beats',
    description: 'First beat, last beat, second, second-last',
    build: (ctx) => beats(ctx, outsideIn()) },
  { id: 'lock-beat-1', name: 'Lock to beat 1', group: 'Beats',
    description: 'One beat looped for the whole pattern — instant stutter bed',
    build: (ctx) => beats(ctx, lockTo(0)) },
  { id: 'beats-alternate', name: 'Alternate beats 1 & 3', group: 'Beats',
    description: 'Bounces between the first and third beats',
    build: (ctx) => beats(ctx, alternate(0, 2)) },

  // ── Stutter & rolls ──────────────────────────────────────────────────────
  {
    id: 'roll-out',
    name: 'Roll out',
    group: 'Rolls',
    description: 'Last four steps retrigger 2, 3, 4 then 6 times, decaying',
    build: (ctx) => {
      const s = base(ctx);
      const counts = [2, 3, 4, 6];
      counts.forEach((count, k) => {
        const st = s[s.length - counts.length + k];
        if (!st) return;
        st.repeat = count;
        st.repeatRamp = -0.4;
        st.gate = 0.9;
      });
      return s;
    },
  },
  {
    id: 'final-bar-stutter',
    name: 'Final bar stutter',
    group: 'Rolls',
    description: 'Whole last bar retriggered four times a step',
    build: (ctx) => {
      const s = base(ctx);
      const from = Math.max(0, s.length - stepsPerBar(ctx));
      for (let i = from; i < s.length; i++) {
        s[i].repeat = 4;
        s[i].repeatRamp = -0.5;
      }
      return s;
    },
  },
  {
    id: 'beat-4-roll',
    name: 'Beat 4 roll',
    group: 'Rolls',
    description: 'A rolled fill on the last beat of every bar',
    build: (ctx) => {
      const s = base(ctx);
      const per = stepsPerBar(ctx);
      s.forEach((st, i) => {
        if (i % per >= per - ctx.division) {
          st.repeat = 3;
          st.repeatRamp = -0.3;
        }
      });
      return s;
    },
  },
  {
    id: 'ratchet-ramp',
    name: 'Ratchet ramp',
    group: 'Rolls',
    description: 'Retriggers climb 1, 2, 3, 4 across each bar',
    build: (ctx) => {
      const s = base(ctx);
      const per = stepsPerBar(ctx);
      s.forEach((st, i) => {
        st.repeat = 1 + Math.floor(((i % per) / per) * 4);
        st.repeatRamp = -0.3;
      });
      return s;
    },
  },
  {
    id: 'random-ratchets',
    name: 'Random ratchets',
    group: 'Rolls',
    description: 'A scattering of steps burst into 2, 3 or 4',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st) => {
        if (Math.random() < 0.25) {
          st.repeat = 2 + Math.floor(Math.random() * 3);
          st.repeatRamp = -0.4;
        }
      });
      return s;
    },
  },
  {
    id: 'building-roll',
    name: 'Building roll',
    group: 'Rolls',
    description: 'Retrigger count climbs from 1 to 8 across the pattern',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => {
        st.repeat = 1 + Math.floor((i / last) * 7);
        st.gain = 0.8 + (i / last) * 0.4;
        st.repeatRamp = 0.2;
      });
      return s;
    },
  },
  {
    id: 'machine-gun',
    name: 'Machine gun',
    group: 'Rolls',
    description: 'Every fourth step bursts into four, hard decay',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 4 === 3) {
          st.repeat = 4;
          st.repeatRamp = -0.6;
        }
      });
      return s;
    },
  },
  {
    id: 'triplet-fill',
    name: 'Triplet fill',
    group: 'Rolls',
    description: 'A three-hit burst on the last step of every bar',
    build: (ctx) => {
      const s = base(ctx);
      const per = stepsPerBar(ctx);
      s.forEach((st, i) => {
        if (i % per === per - 1) {
          st.repeat = 3;
          st.gate = 0.95;
        }
      });
      return s;
    },
  },

  // ── Motion ───────────────────────────────────────────────────────────────
  {
    id: 'alt-reverse',
    name: 'Alternate reverse',
    group: 'Motion',
    description: 'Every other slice played backwards',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 2 === 1) st.reverse = true;
      });
      return s;
    },
  },
  {
    id: 'back-half-reverse',
    name: 'Back half reversed',
    group: 'Motion',
    description: 'Runs forwards, then the whole second half plays backwards',
    build: (ctx) => {
      const s = base(ctx);
      const half = s.length / 2;
      s.forEach((st, i) => {
        if (i >= half) st.reverse = true;
      });
      return s;
    },
  },
  {
    id: 'pitch-drop',
    name: 'Pitch drop',
    group: 'Motion',
    description: 'Slides a full octave down over the length of the pattern',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => (st.pitch = -Math.round((i / last) * 12)));
      return s;
    },
  },
  {
    id: 'pitch-rise',
    name: 'Pitch rise',
    group: 'Motion',
    description: 'Climbs an octave across the pattern',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => (st.pitch = Math.round((i / last) * 12)));
      return s;
    },
  },
  {
    id: 'octave-jumps',
    name: 'Octave jumps',
    group: 'Motion',
    description: 'Every fourth step kicked up an octave',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 4 === 2) st.pitch = 12;
      });
      return s;
    },
  },
  {
    id: 'random-pitch',
    name: 'Random pitch',
    group: 'Motion',
    description: 'Scattered semitone shifts, most steps left alone',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st) => {
        if (Math.random() < 0.35) st.pitch = Math.floor(Math.random() * 11) - 5;
      });
      return s;
    },
  },
  {
    id: 'tape-stop',
    name: 'Tape stop',
    group: 'Motion',
    description: 'Playback rate winds down through the final bar',
    build: (ctx) => {
      const s = base(ctx);
      const per = stepsPerBar(ctx);
      const from = Math.max(0, s.length - per);
      for (let i = from; i < s.length; i++) {
        const t = (i - from) / Math.max(1, s.length - 1 - from);
        s[i].rate = 1 - 0.7 * t;
      }
      return s;
    },
  },
  {
    id: 'filter-rise',
    name: 'Filter rise',
    group: 'Motion',
    description: 'Low pass opens from 300 Hz to wide open across the pattern',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => {
        st.filter = 'lowpass';
        st.cutoff = 300 * Math.pow(60, i / last);
        st.resonance = 3;
      });
      return s;
    },
  },
  {
    id: 'highpass-fall',
    name: 'High pass fall',
    group: 'Motion',
    description: 'Starts thin and lets the low end back in',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => {
        st.filter = 'highpass';
        st.cutoff = 4000 * Math.pow(0.02, i / last);
        st.resonance = 2;
      });
      return s;
    },
  },
  {
    id: 'ping-pong',
    name: 'Ping pong',
    group: 'Motion',
    description: 'Alternates hard left and hard right',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => (st.pan = i % 2 === 0 ? -0.7 : 0.7));
      return s;
    },
  },
  {
    id: 'pan-drift',
    name: 'Pan drift',
    group: 'Motion',
    description: 'One slow sweep across the stereo field',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => (st.pan = Math.sin((i / last) * Math.PI * 2) * 0.8));
      return s;
    },
  },

  // ── Feel ─────────────────────────────────────────────────────────────────
  {
    id: 'swing-feel',
    name: 'Swing',
    group: 'Feel',
    description: 'Offbeats pushed late for a shuffled feel',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 2 === 1) st.nudge = 0.22;
      });
      return s;
    },
  },
  {
    id: 'drunk',
    name: 'Drunk',
    group: 'Feel',
    description: 'Timing wanders either side of the grid',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st) => (st.nudge = (Math.random() - 0.5) * 0.3));
      return s;
    },
  },
  {
    id: 'humanize-gain',
    name: 'Loose dynamics',
    group: 'Feel',
    description: 'Small level and timing variation, nothing structural',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st) => {
        st.gain = Math.max(0, Math.min(2, 1 + (Math.random() - 0.5) * 0.3));
        st.nudge = (Math.random() - 0.5) * 0.08;
      });
      return s;
    },
  },
  {
    id: 'maybe',
    name: 'Maybe',
    group: 'Feel',
    description: 'Offbeats fire 60% of the time — different every pass',
    build: (ctx) => {
      const s = base(ctx);
      s.forEach((st, i) => {
        if (i % 2 === 1) st.probability = 0.6;
      });
      return s;
    },
  },
  {
    id: 'thin-out',
    name: 'Thin out',
    group: 'Feel',
    description: 'Probability falls across the pattern, emptying as it goes',
    build: (ctx) => {
      const s = base(ctx);
      const last = Math.max(1, s.length - 1);
      s.forEach((st, i) => (st.probability = 1 - (i / last) * 0.7));
      return s;
    },
  },

  // ── FX ───────────────────────────────────────────────────────────────────
  { id: 'telephone', name: 'Telephone', group: 'FX',
    description: 'Narrow band pass — everything sounds like it is down a wire',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st) => { st.filter = 'bandpass'; st.cutoff = 1500; st.resonance = 6; }); return s; } },
  { id: 'underwater', name: 'Underwater', group: 'FX',
    description: 'Low pass drifting slowly up and down',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => { const w = Math.sin(progress(i, s.length) * Math.PI * 2);
        st.filter = 'lowpass'; st.cutoff = 400 * Math.pow(9, (w + 1) / 2); st.resonance = 5; }); return s; } },
  { id: 'wah', name: 'Wah', group: 'FX',
    description: 'Resonant sweep, four cycles across the pattern',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => { const w = Math.sin(progress(i, s.length) * Math.PI * 8);
        st.filter = 'bandpass'; st.cutoff = 500 * Math.pow(14, (w + 1) / 2); st.resonance = 9; }); return s; } },
  { id: 'radio-tuning', name: 'Radio tuning', group: 'FX',
    description: 'Band pass hopping about like a dial being turned',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st) => { st.filter = 'bandpass'; st.cutoff = 400 + Math.random() * 5000;
        st.resonance = 4 + Math.random() * 8; }); return s; } },
  { id: 'tremolo', name: 'Tremolo', group: 'FX',
    description: 'Level pumping between full and almost nothing',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => (st.gain = i % 2 === 0 ? 1.1 : 0.35)); return s; } },
  { id: 'auto-pan', name: 'Auto pan', group: 'FX',
    description: 'Smooth sweep across the stereo field, one cycle a bar',
    build: (ctx) => { const s = base(ctx); const per = stepsPerBar(ctx);
      s.forEach((st, i) => (st.pan = Math.sin(((i % per) / per) * Math.PI * 2) * 0.85)); return s; } },
  { id: 'riser', name: 'Riser', group: 'FX',
    description: 'Filter opens and pitch climbs together — a build in one preset',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => { const t = progress(i, s.length);
        st.filter = 'lowpass'; st.cutoff = 400 * Math.pow(40, t); st.resonance = 4;
        st.pitch = Math.round(t * 7); st.gain = 0.85 + t * 0.35; }); return s; } },
  { id: 'faller', name: 'Faller', group: 'FX',
    description: 'Everything sinks — filter closes, pitch drops, level backs off',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => { const t = progress(i, s.length);
        st.filter = 'lowpass'; st.cutoff = 16000 * Math.pow(0.03, t); st.resonance = 3;
        st.pitch = -Math.round(t * 9); st.gain = 1.05 - t * 0.35; }); return s; } },
  { id: 'warble', name: 'Tape warble', group: 'FX',
    description: 'Playback rate wandering slightly, like tired tape',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => (st.rate = 1 + Math.sin(progress(i, s.length) * Math.PI * 6) * 0.06)); return s; } },
  { id: 'sub-drop', name: 'Sub drop', group: 'FX',
    description: 'Downbeats an octave down and filtered dark, the rest untouched',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => { if (i % ctx.division !== 0) return;
        st.pitch = -12; st.filter = 'lowpass'; st.cutoff = 900; st.gain = 1.15; }); return s; } },
  { id: 'air', name: 'Air', group: 'FX',
    description: 'High pass opens and closes — pulls the low end in and out',
    build: (ctx) => { const s = base(ctx);
      s.forEach((st, i) => { const w = Math.sin(progress(i, s.length) * Math.PI * 2);
        st.filter = 'highpass'; st.cutoff = 200 * Math.pow(20, (w + 1) / 2); st.resonance = 2; }); return s; } },

  // ── Characters ───────────────────────────────────────────────────────────
  { id: 'dubwise', name: 'Dubwise', group: 'Characters',
    description: 'Half time, offbeats filtered dark and long — space to drop echo on',
    build: (ctx) => { const s = chain(ctx, ['half-time', 'back-half-reverse']);
      s.forEach((st, i) => { if (i % 2 === 1) { st.filter = 'lowpass'; st.cutoff = 700; st.gain = 0.6; }
        st.gate = 1; }); return s; } },
  { id: 'glitch', name: 'Glitch', group: 'Characters',
    description: 'Random slices, random ratchets, random gates, thrown across the field',
    build: (ctx) => { const s = chain(ctx, ['scatter', 'random-ratchets', 'random-drops']);
      s.forEach((st) => { if (Math.random() < 0.4) st.gate = 0.2 + Math.random() * 0.4;
        if (Math.random() < 0.3) st.pan = Math.random() * 1.6 - 0.8; }); return s; } },
  { id: 'dnb-roller', name: 'DnB roller', group: 'Characters',
    description: 'Ghosted offbeats, thinned low end, rolling out of the last bar',
    build: (ctx) => { const s = chain(ctx, ['ghost', 'roll-out']);
      s.forEach((st, i) => { if (i % 2 === 1) { st.filter = 'highpass'; st.cutoff = 320; } }); return s; } },
  { id: 'footwork', name: 'Footwork', group: 'Characters',
    description: 'Triplet bursts, holes on the offbeats, octaves flying about',
    build: (ctx) => chain(ctx, ['triplet-fill', 'mute-3rd', 'octave-jumps', 'beats-doubled']) },
  { id: 'vaporwave', name: 'Vaporwave', group: 'Characters',
    description: 'Half speed, pitched down, filtered soft, everything ringing on',
    build: (ctx) => { const s = chain(ctx, ['half-time']);
      s.forEach((st) => { st.pitch = -5; st.rate = 0.92; st.gate = 1;
        st.filter = 'lowpass'; st.cutoff = 2600; st.resonance = 1.5; }); return s; } },
  { id: 'dusty', name: 'Dusty', group: 'Characters',
    description: 'Swung, ghosted, a little loose and rolled off — boom bap feel',
    build: (ctx) => { const s = chain(ctx, ['swing-feel', 'ghost', 'humanize-gain']);
      s.forEach((st) => { st.filter = 'lowpass'; st.cutoff = 6500; }); return s; } },
  { id: 'broken', name: 'Broken', group: 'Characters',
    description: 'Bars dealt at random, alternate slices reversed, holes throughout',
    build: (ctx) => chain(ctx, ['bar-shuffle', 'alt-reverse', 'random-drops']) },
  { id: 'build-drop', name: 'Build and drop', group: 'Characters',
    description: 'Ratchets climb and the filter closes, then the last bar stutters out',
    build: (ctx) => { const s = chain(ctx, ['building-roll', 'final-bar-stutter']);
      s.forEach((st, i) => { const t = progress(i, s.length);
        st.filter = 'lowpass'; st.cutoff = 16000 * Math.pow(0.04, t); st.resonance = 6; }); return s; } },
  { id: 'tape-warp', name: 'Tape warp', group: 'Characters',
    description: 'Loose timing, wandering speed, top end gone — a knackered cassette',
    build: (ctx) => chain(ctx, ['drunk', 'warble', 'underwater']) },
  { id: 'hyper', name: 'Hyper', group: 'Characters',
    description: 'Double speed, machine-gun ratchets, pitched up and ping-ponging',
    build: (ctx) => chain(ctx, ['double-time', 'machine-gun', 'ping-pong', 'pitch-rise']) },
  { id: 'ghost-town', name: 'Ghost town', group: 'Characters',
    description: 'Thins out as it goes and drifts off-mic — good for an outro',
    build: (ctx) => chain(ctx, ['thin-out', 'faller', 'pan-drift']) },
  { id: 'stutter-fade', name: 'Stutter fade', group: 'Characters',
    description: 'Chance drops away while the ratchets pile up',
    build: (ctx) => chain(ctx, ['ratchet-ramp', 'thin-out', 'highpass-fall']) },
];

/** The one preset that must always be reachable. */
export const STRAIGHT_PRESET_ID = 'straight';

export const PRESET_GROUPS = [...new Set(PATTERN_PRESETS.map((p) => p.group))];

